import { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

export default function BrokerageManagement() {
  const { user } = useAuth();
  const [brokerages, setBrokerages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const BLANK = { name: '', city: '', state: '', association_id: '', broker_name: '', broker_email: '', want_re: false, want_cre: false, seats_re: 1, seats_cre: 1 };
  const [form, setForm] = useState(BLANK);
  const [associations, setAssociations] = useState([]);
  const [invite, setInvite] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadBrokerages(); loadAssociations(); }, []);

  const loadBrokerages = async () => {
    try {
      const res = await authFetch(`${API}/api/brokerages`);
      if (res.ok) setBrokerages(await res.json());
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAssociations = async () => {
    try {
      const res = await authFetch(`${API}/api/associations`);
      if (res.ok) setAssociations(await res.json());
    } catch (err) { /* associations are optional here */ }
  };

  const selAssoc = associations.find((a) => String(a.id) === String(form.association_id));

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setInvite('');
    try {
      if (selAssoc) {
        // Create under an association (same manual-create flow association admins use).
        if (!(form.want_re || form.want_cre)) { setError('Enable at least one product for this association.'); setCreating(false); return; }
        const body = {
          name: form.name, city: form.city, state: form.state,
          broker_name: form.broker_name, broker_email: form.broker_email,
          want_re: form.want_re, seats_re: form.want_re ? Number(form.seats_re) || 0 : 0,
          want_cre: form.want_cre, seats_cre: form.want_cre ? Number(form.seats_cre) || 0 : 0,
        };
        const res = await authFetch(`${API}/api/associations/${selAssoc.id}/brokerages`, { method: 'POST', body: JSON.stringify(body) });
        const d = await res.json().catch(() => ({}));
        if (res.ok) {
          if (d.invite_path) setInvite(`${window.location.origin}${d.invite_path}`);
          setForm(BLANK);
          loadBrokerages();
        } else {
          setError(d.detail || 'Failed to create brokerage');
        }
      } else {
        // No association — legacy unaffiliated brokerage.
        const res = await authFetch(`${API}/api/brokerages`, {
          method: 'POST',
          body: JSON.stringify({ name: form.name, city: form.city, state: form.state }),
        });
        if (res.ok) {
          setForm(BLANK);
          setShowCreate(false);
          loadBrokerages();
        } else {
          const err = await res.json();
          setError(err.detail || 'Failed to create brokerage');
        }
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and ALL associated agents, buyers, properties, and evaluations? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`${API}/api/brokerages/${id}`, { method: 'DELETE' });
      if (res.ok) loadBrokerages();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-red-400 text-lg">Access denied — Super Admin only</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading brokerages...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Brokerage Management</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">{brokerages.length} brokerage{brokerages.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] transition-colors w-full md:w-auto"
          >
            {showCreate ? 'Cancel' : '+ Add Brokerage'}
          </button>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
        )}

        {showCreate && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 sm:p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">New Brokerage</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Association</label>
                <select value={form.association_id} onChange={(e) => { const a = associations.find((x) => String(x.id) === e.target.value); setForm({ ...form, association_id: e.target.value, want_re: !!(a && a.licensed_re), want_cre: !!(a && a.licensed_cre) }); }}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                  <option value="">None (unaffiliated — direct customer)</option>
                  {associations.map((a) => <option key={a.id} value={a.id}>{a.display_name} ({[a.licensed_re && 'RE', a.licensed_cre && 'CRE'].filter(Boolean).join('+')})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Name *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="Keller Williams Huntsville" required />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">City</label>
                  <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="Huntsville" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">State</label>
                  <input type="text" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="AL" />
                </div>
              </div>
              {selAssoc && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Principal broker name *</label>
                      <input type="text" value={form.broker_name} onChange={(e) => setForm({ ...form, broker_name: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" required />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Broker email *</label>
                      <input type="email" value={form.broker_email} onChange={(e) => setForm({ ...form, broker_email: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" required />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {selAssoc.licensed_re && <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={form.want_re} onChange={(e) => setForm({ ...form, want_re: e.target.checked })} /> RE seats <input type="number" min="0" value={form.seats_re} disabled={!form.want_re} onChange={(e) => setForm({ ...form, seats_re: e.target.value })} className="w-16 px-1 py-0.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white disabled:opacity-40" /></label>}
                    {selAssoc.licensed_cre && <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={form.want_cre} onChange={(e) => setForm({ ...form, want_cre: e.target.checked })} /> CRE seats <input type="number" min="0" value={form.seats_cre} disabled={!form.want_cre} onChange={(e) => setForm({ ...form, seats_cre: e.target.value })} className="w-16 px-1 py-0.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white disabled:opacity-40" /></label>}
                  </div>
                </>
              )}
              <button type="submit" disabled={creating}
                className="px-6 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors w-full sm:w-auto">
                {creating ? 'Creating...' : (selAssoc ? 'Create & Invite Broker' : 'Create Brokerage')}
              </button>
              {invite && (
                <div>
                  <p className="text-xs text-[#B0BEC5] mb-1">Invite link (email is stubbed — send this to the broker):</p>
                  <div className="flex gap-2 items-center">
                    <input readOnly value={invite} className="flex-1 min-w-0 px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-[#00D4FF] text-xs" />
                    <button type="button" onClick={() => navigator.clipboard && navigator.clipboard.writeText(invite)} className="px-3 py-1.5 text-sm bg-[#00D4FF] text-[#0A0E27] font-semibold rounded shrink-0">Copy</button>
                  </div>
                </div>
              )}
            </form>
          </div>
        )}

        {brokerages.length === 0 ? (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
            <p className="text-[#B0BEC5] text-lg">No brokerages yet</p>
          </div>
        ) : (
          <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {brokerages.map(b => (
              <div key={b.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
                <div className="flex justify-between items-start gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-white font-medium truncate">{b.name}</div>
                    {b.white_label_name && <div className="text-gray-500 text-xs">White-label: {b.white_label_name}</div>}
                    <div className="text-gray-300 text-sm mt-1">{b.city}, {b.state}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
                    b.subscription_status === 'active' ? 'bg-green-900/50 text-green-300' :
                    b.subscription_status === 'trial' ? 'bg-blue-900/50 text-blue-300' :
                    'bg-red-900/50 text-red-300'
                  }`}>
                    {b.subscription_status}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-[#546E7A]/20">
                  <div className="text-sm text-gray-300">
                    <span className="text-[#00D4FF] font-medium">{b.agent_count}</span> agents · <span className="text-gray-300">{b.user_count}</span> users
                  </div>
                  <button onClick={() => handleDelete(b.id, b.name)}
                    className="text-gray-400 hover:text-red-400 text-sm transition-colors" title="Delete brokerage">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#546E7A]/30">
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">Brokerage</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">Location</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Agents</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Users</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Status</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {brokerages.map(b => (
                  <tr key={b.id} className="border-b border-[#546E7A]/10 hover:bg-[#243B55]/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{b.name}</div>
                      {b.white_label_name && <div className="text-gray-500 text-xs">White-label: {b.white_label_name}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-300 text-sm">{b.city}, {b.state}</td>
                    <td className="px-4 py-3 text-center text-[#00D4FF] font-medium">{b.agent_count}</td>
                    <td className="px-4 py-3 text-center text-gray-300">{b.user_count}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        b.subscription_status === 'active' ? 'bg-green-900/50 text-green-300' :
                        b.subscription_status === 'trial' ? 'bg-blue-900/50 text-blue-300' :
                        'bg-red-900/50 text-red-300'
                      }`}>
                        {b.subscription_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => handleDelete(b.id, b.name)}
                        className="text-gray-600 hover:text-red-400 text-sm transition-colors" title="Delete brokerage">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
